// src/pages/Randevular.js

import React, { useState, useEffect } from 'react';
import { Box, Button, Typography, Tabs, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Tooltip } from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import ListIcon from '@mui/icons-material/List';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { collection, query, orderBy, onSnapshot, doc, deleteDoc } from 'firebase/firestore';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';

import RandevuTakvimi from '../components/features/RandevuTakvimi';
import GlassCard from '../ui/glass/components/GlassCard';
import StatusChip from '../components/common/StatusChip';
import EmptyState from '../components/common/EmptyState';
import { Loading } from '../components/common/Loading';
import usePermissions from '../hooks/usePermissions';

const Randevular = () => {
    const [tabValue, setTabValue] = useState(0);
    const navigate = useNavigate();
    const { canDelete } = usePermissions();

    // Liste verileri
    const [appointments, setAppointments] = useState([]);
    const [sleepAppointments, setSleepAppointments] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // Normal Randevular
        const qApp = query(collection(db, "appointments"), orderBy("randevuTarihi", "desc"));
        const unsubApp = onSnapshot(qApp, (snapshot) => {
            setAppointments(snapshot.docs.map(doc => ({ id: doc.id, type: 'randevu', ...doc.data() })));
            setLoading(false);
        });

        // Uyku Takip Randevuları
        const qSleep = query(collection(db, "sleepAppointments"), orderBy("olusturmaTarihi", "desc"));
        const unsubSleep = onSnapshot(qSleep, (snapshot) => {
            setSleepAppointments(snapshot.docs.map(doc => ({ id: doc.id, type: 'uyku', ...doc.data() })));
        });

        return () => {
            unsubApp();
            unsubSleep();
        };
    }, []);

    // İki listeyi birleştir ve tarihe göre sırala
    const allAppointments = [...appointments, ...sleepAppointments].sort((a, b) => {
        const dateA = a.randevuTarihi?.toDate ? a.randevuTarihi.toDate() : (a.randevuTarihi || 0);
        const dateB = b.randevuTarihi?.toDate ? b.randevuTarihi.toDate() : (b.randevuTarihi || 0);
        return dateB - dateA;
    });

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const handleNewClick = () => {
        navigate('/randevu/yeni');
    };

    const handleEventClick = (randevuData) => {
        if (randevuData && randevuData.id) {
            if (randevuData.type === 'uyku') {
                navigate(`/uyku-takip/${randevuData.id}`);
            } else {
                navigate(`/randevu/${randevuData.id}`);
            }
        }
    };

    const handleEditClick = (id, type) => {
        if (type === 'uyku') {
            navigate(`/uyku-takip/${id}`);
        } else {
            navigate(`/randevu/${id}`);
        }
    };

    const handleDelete = async (e, id, type) => {
        e.stopPropagation();
        if (window.confirm("Bu kaydı silmek istediğinizden emin misiniz?")) {
            try {
                const collectionName = type === 'uyku' ? 'sleepAppointments' : 'appointments';
                await deleteDoc(doc(db, collectionName, id));
            } catch (error) {
                console.error("Silme hatası:", error);
                alert("Silinirken hata oluştu.");
            }
        }
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return '-';
        // Timestamp veya Date objesi kontrolü
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return format(date, 'dd.MM.yyyy HH:mm', { locale: tr });
    };

    return (
        <Box sx={{ p: { xs: 1, md: 1 }, height: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
            <GlassCard sx={{ p: 2, flexShrink: 0 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                    <Box>
                        <Typography variant="h5" component="h1" fontWeight="bold" color="primary.main">Randevu Yönetimi</Typography>
                        <Tabs value={tabValue} onChange={handleTabChange} aria-label="randevu tabs">
                            <Tab icon={<EventIcon />} iconPosition="start" label="Takvim" />
                            <Tab icon={<ListIcon />} iconPosition="start" label="Liste" />
                        </Tabs>
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleNewClick}
                    >
                        Yeni Randevu
                    </Button>
                </Box>
            </GlassCard>

            <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
                {tabValue === 0 && (
                    <GlassCard sx={{ height: '100%', p: 2, overflow: 'auto' }}>
                        <RandevuTakvimi onEventClick={handleEventClick} />
                    </GlassCard>
                )}
                {tabValue === 1 && (
                    <GlassCard sx={{ height: '100%', p: 0, overflow: 'auto' }}>
                        {loading ? (
                            <Loading message="Randevular yükleniyor..." />
                        ) : (
                            <TableContainer>
                                <Table stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Tarih</TableCell>
                                            <TableCell>Müşteri</TableCell>
                                            <TableCell>Telefon</TableCell>
                                            <TableCell>Hizmet</TableCell>
                                            <TableCell>Cihaz</TableCell>
                                            <TableCell>Notlar</TableCell>
                                            <TableCell align="center">İşlemler</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {allAppointments.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={7} sx={{ border: 0 }}>
                                                    <EmptyState icon={<EventIcon />} message="Randevu bulunamadı." />
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            allAppointments.map((app) => (
                                                <TableRow
                                                    key={app.id}
                                                    hover
                                                    onClick={() => handleEditClick(app.id, app.type)}
                                                    sx={{
                                                        cursor: 'pointer',
                                                        bgcolor: app.type === 'uyku' ? 'rgba(156, 39, 176, 0.04)' : 'inherit' // Hafif mor arka plan
                                                    }}
                                                >
                                                    <TableCell>{formatDate(app.randevuTarihi)}</TableCell>
                                                    <TableCell sx={{ fontWeight: 600 }}>{app.musteriAdSoyad}</TableCell>
                                                    <TableCell>{app.musteriTelefon}</TableCell>
                                                    <TableCell>
                                                        <StatusChip
                                                            label={app.type === 'uyku' ? 'Uyku Takip' : app.ilgilenilenHizmet}
                                                            color={app.type === 'uyku' ? 'secondary' : 'default'}
                                                        />
                                                    </TableCell>
                                                    <TableCell>{app.marka ? `${app.marka} ${app.model || ''}` : '-'}</TableCell>
                                                    <TableCell sx={{ maxWidth: 200, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                        {app.notlar || '-'}
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                                                            <Tooltip title="Düzenle">
                                                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleEditClick(app.id); }}>
                                                                    <EditIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                            {canDelete() && (
                                                                <Tooltip title="Sil">
                                                                    <IconButton size="small" color="error" onClick={(e) => handleDelete(e, app.id)}>
                                                                        <DeleteIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            )}
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </GlassCard>
                )}
            </Box>
        </Box>
    );
};

export default Randevular;